import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import StatusIndicator from '../StatusIndicator';
import MainResult from '../MainResult';
import NotFound from '../NotFound';
import { submitGenerationJob, getJobStatus, getHistoryById } from '../../services/apiService';
import { selectPendingPromptData } from '../../store/selectors';
import { setPendingPromptData, clearPendingPromptData } from '../../store/promptSlice';

function ResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { requestId, imageId } = useParams();
  const pendingPromptData = useSelector(selectPendingPromptData);

  const [status, setStatus] = useState({ status: 'queued', progress: 0 });
  const [showLoading, setShowLoading] = useState(false);
  const [showMainResult, setShowMainResult] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [mainResultImages, setMainResultImages] = useState([]);
  const [isJobComplete, setIsJobComplete] = useState(false);
  const [lastDisplayedCount, setLastDisplayedCount] = useState(0);

  const hasSubmittedRef = useRef(false);
  const loadedRequestIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isLoadingRequestRef = useRef(false);
  
  // Function to refresh images from history (for updating saved status after collection changes)
  // IMPORTANT: Only update the 'saved' status of existing images, do NOT replace the entire array
  // because the array may contain Kura Ads that are not stored in the database
  const refreshImagesFromHistory = async () => {
    if (!requestId) return;
    
    try {
      const response = await getHistoryById(requestId);
      if (response.success && response.data && response.data.images) {
        // Create a map of image ID -> saved status from database
        const savedStatusMap = new Map();
        response.data.images.forEach((img) => {
          if (img.id) {
            savedStatusMap.set(img.id, img.saved || false);
          }
        });
        
        // Update only the 'saved' status of existing images, preserving Kura Ads
        setMainResultImages(prevImages => 
          prevImages.map(img => {
            // If this image has an ID and we have updated saved status for it, update it
            if (img.id && savedStatusMap.has(img.id)) {
              return { ...img, saved: savedStatusMap.get(img.id) };
            }
            // Otherwise keep the image as-is (including Kura Ads which have no ID in database)
            return img;
          })
        );
      }
    } catch (error) {
      console.error('Error refreshing images:', error);
    }
  };

  const pollJobStatus = (jobId) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    let hasShownResults = false;

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const jobStatus = await getJobStatus(jobId);

        if (!hasShownResults) {
          if (!jobStatus.results || jobStatus.results.length === 0) {
            setStatus(jobStatus);
          } else if (jobStatus.results.length >= 6 && jobStatus.status !== 'completed') {
            setStatus({ ...jobStatus, progress: 100 });

            await new Promise(resolve => setTimeout(resolve, 800));

            const images = jobStatus.results.map(img => ({
              url: typeof img === 'string' ? img : img.url,
              prompt: img.prompt || jobStatus.generatedPrompt || '',
              timestamp: img.timestamp || jobStatus.completedAt || '',
              aspectRatio: '4/3',
              id: img.id,
              isAd: img.isAd,
              link: img.link,
              title: img.title
            }));

            setMainResultImages(images);
            setLastDisplayedCount(jobStatus.results.length);
            setShowLoading(false);
            setShowMainResult(true);
            hasShownResults = true;
          } else {
            setStatus(jobStatus);
          }
        }

        // Update images when on MainResult page (after first batch shown)
        if (hasShownResults && jobStatus.results && jobStatus.results.length > lastDisplayedCount) {
          const images = jobStatus.results.map(img => ({
            url: typeof img === 'string' ? img : img.url,
            prompt: img.prompt || jobStatus.generatedPrompt || '',
            timestamp: img.timestamp || jobStatus.completedAt || '',
            aspectRatio: '4/3',
            // Pass metadata
            isAd: img.isAd,
            link: img.link,
            title: img.title
          }));

          setMainResultImages(images);
          setLastDisplayedCount(jobStatus.results.length);
        }

        if (jobStatus.status === 'completed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsJobComplete(true);
          loadedRequestIdRef.current = jobId;

          if (!jobStatus.results || jobStatus.results.length === 0) {
            setShowLoading(false);
            setShowNotFound(true);
          } else {
            const images = jobStatus.results.map(img => ({
              url: typeof img === 'string' ? img : img.url,
              prompt: img.prompt || jobStatus.generatedPrompt || '',
              timestamp: img.timestamp || jobStatus.completedAt || '',
              aspectRatio: '4/3',
              id: img.id,
              isAd: img.isAd,
              link: img.link,
              title: img.title
            }));

            setMainResultImages(images);
            setLastDisplayedCount(jobStatus.results.length);

            if (!hasShownResults) {
              setShowLoading(false);
              setShowMainResult(true);
              hasShownResults = true;
            }
          }
        } else if (jobStatus.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setShowLoading(false);
          setShowNotFound(true);
        }
      } catch (error) {
        console.error('Error polling status:', error);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setShowLoading(false);
        setShowNotFound(true);
      }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleError = () => {
      setShowNotFound(true);
      setShowLoading(false);
      isLoadingRequestRef.current = false;
    };

    const loadRequestFromHistory = async (requestIdToLoad) => {
      if (loadedRequestIdRef.current === requestIdToLoad) {
        isLoadingRequestRef.current = false;
        return;
      }

      try {
        const response = await getHistoryById(requestIdToLoad);
        if (!response.success || !response.data) {
          handleError();
          return;
        }

        const data = response.data;
        const convertedData = {
          projectName: data.projectName,
          idea: data.idea,
          fields: data.fields || [],
          availableTags: data.availableTags || [],
          selectedTag: data.selectedTag,
          dynamicInput: data.dynamicInput,
          for: data.for || null,
          color: data.color || null,
          style: data.style || null,
          typography: data.typography || null,
          size: data.size || null,
          mood: data.mood || null,
          layout: data.layout || null,
          material: data.material || null,
          assets: data.assets || [],
        };
        
        dispatch(setPendingPromptData(convertedData));
        setStatus({ status: 'loading', progress: 50 });
        
        if (data.images && Array.isArray(data.images) && data.images.length > 0) {
          const formattedImages = data.images.map((img) => ({
            url: img.url || '',
            prompt: data.generatedPrompt || '',
            timestamp: img.createdAt || img.created_at || '',
            aspectRatio: '4/3',
            id: img.id || '',
            saved: img.saved || false, // Use saved field from backend
          })).filter(img => img.url && img.id);
          
          setMainResultImages(formattedImages);
          setStatus({ status: 'completed', progress: 100 });
          await new Promise(resolve => setTimeout(resolve, 300));
          setShowLoading(false);
          setShowMainResult(true);
          setIsJobComplete(true);
          loadedRequestIdRef.current = requestIdToLoad;
          isLoadingRequestRef.current = false;
        } else {
          handleError();
        }
      } catch (error) {
        console.error('Error loading request data:', error);
        handleError();
      }
    };

    const initializeResultPage = async () => {
      if (requestId && requestId !== 'undefined') {
        // If imageId is present, stop polling and load from history directly
        if (imageId) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Load from history to get the images and prompt data
          if (loadedRequestIdRef.current !== requestId || !showMainResult) {
            setShowLoading(true);
            setShowNotFound(false);
            setShowMainResult(false);
            setStatus({ status: 'loading', progress: 0 });
            isLoadingRequestRef.current = true;
            dispatch(clearPendingPromptData());
            await loadRequestFromHistory(requestId);
          }
          return;
        }

        if (loadedRequestIdRef.current === requestId && isJobComplete && showMainResult) {
          return;
        }

        if (isLoadingRequestRef.current) {
          return;
        }

        isLoadingRequestRef.current = true;
        if (loadedRequestIdRef.current !== requestId) {
          setShowLoading(true);
          setShowNotFound(false);
          setShowMainResult(false);
          setStatus({ status: 'loading', progress: 0 });
        }

        const shouldLoadFromHistory = location.state?.fromHistory;

        if (shouldLoadFromHistory) {
          dispatch(clearPendingPromptData());
          await loadRequestFromHistory(requestId);
          return;
        }

        if (pollingIntervalRef.current) {
          isLoadingRequestRef.current = false;
          return;
        }

        try {
          const jobStatus = await getJobStatus(requestId);
          
          if (jobStatus.status === 'completed') {
            dispatch(clearPendingPromptData());
            await loadRequestFromHistory(requestId);
          } else if (jobStatus.status === 'in_progress' || jobStatus.status === 'queued') {
            isLoadingRequestRef.current = false;
            if (!pollingIntervalRef.current) {
              pollJobStatus(requestId);
            }
          } else {
            handleError();
          }
        } catch (error) {
          if (error.response?.status === 404) {
            if (pollingIntervalRef.current) {
              isLoadingRequestRef.current = false;
              return;
            }
            console.log('Job not found in queue, fetching from history:', requestId);
            await loadRequestFromHistory(requestId);
          } else {
            console.error('Error checking job status:', error);
            handleError();
          }
        }
      } else {
        // Case 2: No requestId in URL - create new request
        await submitNewJob();
      }
    };

    const submitNewJob = async () => {
      if (!pendingPromptData) {
        navigate('/', { replace: true });
        return;
      }

      if (hasSubmittedRef.current) return;

      setShowLoading(true);
      setShowNotFound(false);
      setShowMainResult(false);

      setIsJobComplete(false);
      setLastDisplayedCount(0);
      setMainResultImages([]);
      setStatus({ status: 'queued', progress: 0 });

      hasSubmittedRef.current = true;

      try {
        const response = await submitGenerationJob(pendingPromptData);
        
        if (!response || !response.jobId) {
          console.error('No jobId returned from submitGenerationJob');
          setShowLoading(false);
          setShowNotFound(true);
          hasSubmittedRef.current = false;
          return;
        }
        
        // Update Redux state with the generated project name
        if (response.projectName && pendingPromptData) {
          dispatch(setPendingPromptData({
            ...pendingPromptData,
            projectName: response.projectName,
          }));
        }
        
        navigate(`/result/${response.jobId}`, { replace: true });
        
        pollJobStatus(response.jobId);
      } catch (error) {
        console.error('Error starting generation:', error);
        setShowLoading(false);
        setShowNotFound(true);
        hasSubmittedRef.current = false;
      }
    };

    initializeResultPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, imageId, pendingPromptData, dispatch, navigate, isJobComplete, showMainResult]);

  const handleBackToForm = () => {
    dispatch(clearPendingPromptData());
    navigate('/');
  };

  const handleRegenerate = (updatedDataOrPrompt) => {
    const normalizedUpdates = { ...updatedDataOrPrompt };
    
    delete normalizedUpdates.dynamic_input;
    
    const arrayFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material', 'availableTags'];
    arrayFields.forEach(field => {
      if (normalizedUpdates[field] !== undefined) {
        if (!Array.isArray(normalizedUpdates[field]) && normalizedUpdates[field] !== null) {
          // Convert string to array
          normalizedUpdates[field] = [normalizedUpdates[field]];
        }
      }
    });

    let updatedData;
    if (pendingPromptData) {
      updatedData = {
        projectName: pendingPromptData.projectName,
        idea: pendingPromptData.idea,
        fields: pendingPromptData.fields || [],
        // availableTags: preserve if not in updates, otherwise use updates
        ...(normalizedUpdates.availableTags !== undefined 
          ? { availableTags: normalizedUpdates.availableTags }
          : (pendingPromptData.availableTags ? { availableTags: pendingPromptData.availableTags } : {})
        ),
      };

      const presetDynamicFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material', 'style'];
      presetDynamicFields.forEach(field => {
        if (normalizedUpdates[field] !== undefined) {
          updatedData[field] = normalizedUpdates[field];
        } else if (pendingPromptData[field] !== undefined) {
          updatedData[field] = pendingPromptData[field];
        }
      });

      // Handle dynamicInput - use from updates if present, otherwise preserve existing
      if (normalizedUpdates.dynamicInput !== undefined) {
        updatedData.dynamicInput = normalizedUpdates.dynamicInput;
      } else if (pendingPromptData.dynamicInput !== undefined) {
        updatedData.dynamicInput = pendingPromptData.dynamicInput;
      }

      // Handle assets - use from updates if present, otherwise preserve existing
      if (normalizedUpdates.assets !== undefined) {
        updatedData.assets = normalizedUpdates.assets;
      } else if (pendingPromptData.assets !== undefined) {
        updatedData.assets = pendingPromptData.assets;
      }

      // Preserve selectedTag from updates if present, otherwise keep old one
      if (normalizedUpdates.selectedTag !== undefined) {
        updatedData.selectedTag = normalizedUpdates.selectedTag;
      } else if (pendingPromptData.selectedTag !== undefined) {
        updatedData.selectedTag = pendingPromptData.selectedTag;
      }
    } else {
      updatedData = normalizedUpdates;
    }

    dispatch(setPendingPromptData(updatedData));
    hasSubmittedRef.current = false;
    
    navigate('/result', { replace: true });
  };

  return (
    <>
      {showMainResult && (
        <div className="App">
          <MainResult
            images={mainResultImages}
            onRegenerate={handleRegenerate}
            isLoading={!isJobComplete}
            totalExpected={status?.totalExpected}
            onImageUpdate={refreshImagesFromHistory}
          />
        </div>
      )}

      {showLoading && (
        <div className="loading-container">
          <div className="loading-content">
            <StatusIndicator status={status} />
          </div>
        </div>
      )}

      {showNotFound && (
        <div className="App">
          <NotFound onBack={handleBackToForm} />
        </div>
      )}
    </>
  );
}

export default ResultPage;
